import {
  ADDITIONAL_DISPLAY_TYPES,
  COLOR_CODES,
  DFM_DESCRIPTIONS,
  FAMILY_MAP,
  FEATURE_PROP_TYPES,
  KEYS,
  QUICK_DESCRIPTION,
  SPLIT_CHAR,
} from './constants';
import { objectWithoutProperties } from './utils';

const DB_INFO_MAP = {
  volume: { type: FEATURE_PROP_TYPES.LENGTH, order: 3 },
  area: { type: FEATURE_PROP_TYPES.LENGTH, order: 2 },
  cut_depth: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  cut_length: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  depth: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  diameter: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  dist: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  length: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  local_depth: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  major_diameter: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  max_depth: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  max_height: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  max_length: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  max_radius: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  max_width: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  min_radius: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  minor_diameter: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  offset_height: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  radius: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  size_x: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  size_y: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  stock_length: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  stock_radius: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  thickness: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  width: { type: FEATURE_PROP_TYPES.LENGTH, order: 1 },
  angle: { type: FEATURE_PROP_TYPES.ANGLE, order: null },
  aspect_ratio: {
    type: FEATURE_PROP_TYPES.NON_DIMENSIONAL_NUMERIC,
    order: null,
  },
  depth_to_tool_diameter: {
    type: FEATURE_PROP_TYPES.NON_DIMENSIONAL_NUMERIC,
    order: null,
  },
  distance_ratio: {
    type: FEATURE_PROP_TYPES.NON_DIMENSIONAL_NUMERIC,
    order: null,
  },
  k_factor: { type: FEATURE_PROP_TYPES.NON_DIMENSIONAL_NUMERIC, order: null },
  length_to_min_diameter: {
    type: FEATURE_PROP_TYPES.NON_DIMENSIONAL_NUMERIC,
    order: null,
  },
  nx: { type: FEATURE_PROP_TYPES.NON_DIMENSIONAL_NUMERIC, order: null },
  ratio: { type: FEATURE_PROP_TYPES.NON_DIMENSIONAL_NUMERIC, order: null },
  relief_depth_to_diameter: {
    type: FEATURE_PROP_TYPES.NON_DIMENSIONAL_NUMERIC,
    order: null,
  },
  bottom_type: { type: FEATURE_PROP_TYPES.STRING, order: null },
  has_transition: { type: FEATURE_PROP_TYPES.BOOL, order: null },
  requires_piercing: { type: FEATURE_PROP_TYPES.BOOL, order: null },
  label: { type: FEATURE_PROP_TYPES.STRING, order: null },
  vector: { type: FEATURE_PROP_TYPES.UNIT_VECTOR, order: null },
  pointer: { type: FEATURE_PROP_TYPES.UNIT_VECTOR, order: null },
};

const constructDbDataFromLegacyFeature = (feature) => {
  const out = [];
  for (const key in feature) {
    if (DB_INFO_MAP[key] && feature[key] != null) {
      // perform smart conversion of angles. If the angle is greater than pi, than it likely
      // is stored in degress, so we will convert it here because it will be auto converted
      // as if it were in radians downstream. Moving forward, all stored angle properties
      // will be in radians
      let value = feature[key];
      if (
        DB_INFO_MAP[key].type === FEATURE_PROP_TYPES.ANGLE &&
        value > Math.PI + 0.01
      ) {
        value = (value * Math.PI) / 180;
      }
      out.push({ name: key, value, ...DB_INFO_MAP[key] });
    }
  }
  return out;
};

const constructAdditionalViewerContentInfoFromLegacyFeature = (feature) => {
  const out = {};
  if (feature.vector) {
    out.vector = ADDITIONAL_DISPLAY_TYPES.ARROW;
  }
  if (feature.pointer) {
    out.pointer = ADDITIONAL_DISPLAY_TYPES.ARROW;
  }
  return out;
};

const constructViewerContentFromLegacyFeature = (feature) => {
  return objectWithoutProperties(feature, [
    'desc',
    'type',
    'faces',
    'edges',
    'id',
    'viewpoint',
  ]);
};

const constructRawFeatureIdFromLegacyFeature = (f, documentHash) => {
  if (f.id != null) {
    return `${documentHash}${SPLIT_CHAR}${f.id}`;
  } else {
    return `${documentHash}${SPLIT_CHAR}${f.type}${f.faces}${f.edges}`;
  }
};

const constructRawFeedbackIdFromLegacyFeature = (f, documentHash) => {
  if (f.id != null) {
    return `${documentHash}${SPLIT_CHAR}${f.id}`;
  } else {
    return `${documentHash}${SPLIT_CHAR}${f.desc}${f.type}${f.faces}${f.edges}`;
  }
};

export const prepareRawFeaturePriorToFeatureSchema = (
  rawFeature,
  documentHash,
  referenceUuid,
  isInnate,
) => {
  const dbData = constructDbDataFromLegacyFeature(rawFeature);
  const additionalViewerContentInfo =
    constructAdditionalViewerContentInfoFromLegacyFeature(rawFeature);
  const viewerContent = constructViewerContentFromLegacyFeature(rawFeature);
  const rawFeatureId = constructRawFeatureIdFromLegacyFeature(
    rawFeature,
    documentHash,
  );
  const key = `${referenceUuid}${SPLIT_CHAR}${rawFeatureId}`;

  const label = (dbData.find((x) => x.name === 'label') || {}).value;
  const { viewpoint } = rawFeature;
  return {
    key,
    id: rawFeatureId,
    isInnate,
    referenceUuid,
    name: rawFeature.type,
    message: DFM_DESCRIPTIONS[rawFeature.type],
    faces: rawFeature.faces,
    edges: rawFeature.edges,
    color: COLOR_CODES[label]
      ? COLOR_CODES[label]
      : COLOR_CODES[FAMILY_MAP[rawFeature.type]],
    family: FAMILY_MAP[rawFeature.type],
    canSnap: !!viewpoint,
    cameraPosition: viewpoint && viewpoint.position,
    controlTarget: viewpoint && viewpoint.target,
    canTag: false,
    dbData,
    derivedFeatureIds: [],
    derivedFeatures: [],
    parentFeature: null,
    additionalViewerContentInfo,
    additionalViewerContent: viewerContent,
    isLegacy: true,
  };
};

export const prepareRawFeedbackPriorToFeatureSchema = (
  rawFeedback,
  documentHash,
  referenceUuid,
  isInnate,
) => {
  const dbData = constructDbDataFromLegacyFeature(rawFeedback);
  const additionalViewerContentInfo =
    constructAdditionalViewerContentInfoFromLegacyFeature(rawFeedback);
  const viewerContent = constructViewerContentFromLegacyFeature(rawFeedback);
  const rawFeedbackId = constructRawFeedbackIdFromLegacyFeature(
    rawFeedback,
    documentHash,
  );
  const key = `${referenceUuid}${SPLIT_CHAR}${rawFeedbackId}`;

  const { viewpoint } = rawFeedback;
  return {
    key,
    id: rawFeedbackId,
    isInnate,
    referenceUuid,
    name: rawFeedback.desc,
    message: DFM_DESCRIPTIONS[rawFeedback.desc],
    faces: rawFeedback.faces,
    edges: rawFeedback.edges,
    exclusion: rawFeedback.type === KEYS.exclusion,
    color: rawFeedback.type === KEYS.exclusion ? 0xff4136 : 0xffdc00,
    family: QUICK_DESCRIPTION[rawFeedback.desc],
    canSnap: !!viewpoint,
    cameraPosition: viewpoint && viewpoint.position,
    controlTarget: viewpoint && viewpoint.target,
    canTag: false,
    dbData,
    derivedFeedbackIds: [],
    derivedFeatures: [],
    additionalViewerContentInfo,
    additionalViewerContent: viewerContent,
    isLegacy: true,
  };
};

export const LEGACY_TREE_BRANCHES = [
  'milling',
  'lathe',
  'flat',
  'laser_cut',
  'water_jet',
  'wire_edm',
  'additive',
  'cast_urethane',
  'additive',
  'sheet_metal',
];

const getRawDataFromLegacyFeatureTree = (tree, branch, type) => {
  if (!tree) return [];
  if (branch === 'milling' || branch === 'mill3') {
    return (tree && tree?.machining?.milling?.[type]) ?? [];
  } else if (branch === 'laser_cut') {
    // ToDo: remove with reinterrogation
    // here is a temporary fix for malignant flat parts partviewer documents
    if ('laser_cut' in tree.flat) {
      return (tree && tree?.flat?.laser_cut?.[type]) ?? [];
    } else {
      return (tree && tree?.flat?.[type]) ?? [];
    }
  } else if (branch === 'water_jet') {
    // ToDo: remove with reinterrogation
    // here is a temporary fix for malignant flat parts partviewer documents
    if ('water_jet' in tree.flat) {
      return (tree && tree?.flat?.water_jet?.[type]) ?? [];
    } else {
      return [];
    }
  } else if (branch === 'wire_edm') {
    if ('wire_edm' in tree) {
      return (tree && tree?.wire_edm?.[type]) ?? [];
    } else {
      return [];
    }
  } else if (branch === 'flat') {
    return (tree && tree?.flat?.[type]) ?? [];
  } else if (branch === 'lathe') {
    return (tree && tree?.machining?.lathe?.[type]) ?? [];
  } else if (branch === 'sheet_metal') {
    return (
      (tree &&
        (tree.sheet_metal?.[type] ??
          tree.flat?.laser_cut?.[type] ??
          tree.flat?.[type])) ??
      []
    );
  } else if (branch === 'cast_urethane' || branch === 'casting') {
    return (tree && tree?.cast_urethane?.[type]) ?? [];
  } else if (branch === 'additive') {
    return (tree && tree?.additive?.[type]) ?? [];
  } else if (branch === 'part') {
    return (tree && tree?.[type]) ?? [];
  } else {
    return [];
  }
};

export const getFeedbackFromFeatureTree = (data, branch, referenceUuid) => {
  if (!data) {
    return [];
  }
  if (!data.tree) {
    return [];
  }
  const info = getRawDataFromLegacyFeatureTree(data.tree, branch, 'feedback');
  const out = [];
  for (const f of info) {
    out.push(
      prepareRawFeedbackPriorToFeatureSchema(f, '', referenceUuid, true),
    );
  }
  return out;
};

export const getFeaturesFromFeatureTree = (data, branch, referenceUuid) => {
  if (!data) {
    return [];
  }
  if (!data.tree) {
    return [];
  }
  const info = getRawDataFromLegacyFeatureTree(data.tree, branch, 'features');
  const out = [];
  for (const f of info) {
    out.push(prepareRawFeaturePriorToFeatureSchema(f, '', referenceUuid, true));
  }
  return out;
};
